import React from 'react';
import { connect } from 'react-redux';
import * as R from 'ramda';
import { Link } from 'react-router-dom';
import ScrollArea from 'react-scrollbar';
import moment from 'moment';
import { pure } from 'recompose';

import IconLeft from 'material-ui/svg-icons/navigation/chevron-left';
import IconRight from 'material-ui/svg-icons/navigation/chevron-right';
import MUICheckbox from 'material-ui/Checkbox';

import { Grid, GridHeader, GridFooter, GridBody, GridRow, GridCell } from '../../components/basic/grid';
import PlainTextField from '../../components/basic/PlainTextField';
import IconButton from '../../components/basic/IconButton';
import StatusBadge from '../../components/results/StatusBadge';
import ScoreChart from '../../components/results/ScoreChart';

import * as resultsActions from '../../actions/entities/ResultActions';

const trimToNull = (val) => {
  if (!val) {
    return null;
  }
  const trimmed = val.trim();
  if (!trimmed) {
    return null;
  }
  return trimmed;
};

const determineDateRange = (from, to) => {
  if (!from && !to) {
    return [];
  }
  const fromMillis = from ? new Date(from).getTime() : 0;
  const toMillis = to ? new Date(to).getTime() : Date.now();
  return [fromMillis, toMillis];
};

const Checkbox = ({ iconStyle, labelStyle, ...props }) => {
  return (
    <MUICheckbox
      labelStyle={{ fontWeight: 400, whiteSpace: 'nowrap', ...labelStyle }}
      iconStyle={{ marginRight: 8, ...iconStyle }}
      {...props}
    />
  );
};

const FilterPanelLabel = (props) => <small className="text-muted">{props.children}</small>;

const ACTIVITY_STATUSES = ['REGISTERED', 'IN_PROGRESS', 'FINISHED'];
const ActivityStatusPanel = (props) => {
  const { statuses, onToggle } = props;
  return (
    <div>
      <div
        style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', padding: '8px 16px' }}
      >
        <FilterPanelLabel>Status:</FilterPanelLabel>
        {R.map(
          (s) => (
            <div key={s}>
              <Checkbox
                label={<StatusBadge status={s} />}
                onCheck={() => onToggle(s)}
                checked={!!statuses[s]}
                style={{ marginLeft: '16px' }}
              />
            </div>
          ),
          ACTIVITY_STATUSES
        )}
      </div>
    </div>
  );
};

const ASSESSMENT_TYPES = [
  { key: 'cultureFit', label: 'Culture Fit' },
  { key: 'manSim', label: 'The Game Changer' },
  { key: 'officeDay', label: 'The Office Day' },
  { key: 'echo', label: 'ECHO' },
];
const AssessmentTypePanel = (props) => {
  const { assessments, onToggle } = props;
  return (
    <div>
      <div
        style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', padding: '8px 16px' }}
      >
        <FilterPanelLabel>Activity:</FilterPanelLabel>
        {R.map(
          (s) => (
            <div key={s.key}>
              <Checkbox
                label={s.label}
                onCheck={() => onToggle(s.key)}
                checked={!!assessments[s.key]}
                style={{ marginLeft: '16px' }}
              />
            </div>
          ),
          ASSESSMENT_TYPES
        )}
      </div>
    </div>
  );
};

const SimpleDateFilterPanel = (props) => {
  const { label, from, to, onFromChange, onToChange } = props;
  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', padding: '0 16px' }}>
      <FilterPanelLabel>{label}</FilterPanelLabel>
      <PlainTextField variant="mini" value={from} onChange={onFromChange} type="date" />
      <PlainTextField variant="mini" value={to} onChange={onToChange} type="date" />
    </div>
  );
};
const SimpleInputFilterPanel = (props) => {
  const { label, placeholder, value, onChange } = props;
  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', padding: '0 16px' }}>
      <FilterPanelLabel>{label}</FilterPanelLabel>
      <PlainTextField variant="mini" value={value} placeholder={placeholder} onChange={onChange} />
    </div>
  );
};
const ProjectFilterPanel = (props) => (
  <SimpleInputFilterPanel label="Project:" placeholder="Search by project name" {...props} />
);
const CompanyFilterPanel = (props) => (
  <SimpleInputFilterPanel label="Company:" placeholder="Search by company name" {...props} />
);

const Row = pure(({ id, participant, project, statusTimestamp, status, result, simulation }) => (
  <Link to={`/data/results/${project.id}/${id}/${simulation.id}`}>
    <GridRow className="row">
      <GridCell className="col-sm-4">
        <div style={{ float: 'left', display: 'inline-block', fontSize: 0, marginRight: '8px' }}>
          <ScoreChart result={result} />
        </div>
        {participant.lastName || participant.firstName ? (
          <span>{`${participant.lastName} ${participant.firstName}`}</span>
        ) : (
          <em>Unnamed</em>
        )}
        <br />
        <small className="font-weight-light">{participant.email}</small>
      </GridCell>
      <GridCell className="col-sm-4">
        <span>
          {project.company.name} / {project.name}
        </span>
        <br />
        <small>{project.activity.name}</small>
      </GridCell>
      <GridCell className="col-sm-1">
        <span>
          <StatusBadge status={status} />
        </span>
      </GridCell>
      <GridCell className="col-sm-3">
        <span>{moment(statusTimestamp).format()}</span>
      </GridCell>
    </GridRow>
  </Link>
));

const PAGE_SIZE = 50;

class ResultsContainer extends React.Component {
  state = { filter: '', statuses: {}, assessments: {}, projectFilterValue: '', companyFilterValue: '' };

  componentDidMount() {
    this.loadResults();
  }

  componentWillUnmount() {
    if (this._filterChangeTimeout) {
      clearTimeout(this._filterChangeTimeout);
    }
  }

  loadResults = (page) => {
    const { fetchResults } = this.props;
    const {
      filter,
      statuses,
      assessments,
      projectFilterValue,
      companyFilterValue,
      completedFrom,
      completedTo,
    } = this.state;
    const additionalQuery = {};
    const activityKeys = Object.keys(assessments);
    const statusValues = Object.keys(statuses);
    if (statusValues.length) {
      additionalQuery.status = statusValues;
    }
    if (activityKeys.length) {
      additionalQuery['project.activity.key'] = activityKeys;
    }
    const pfv = trimToNull(projectFilterValue);
    if (pfv) {
      additionalQuery['project.name'] = pfv;
    }
    const cfv = trimToNull(companyFilterValue);
    if (cfv) {
      additionalQuery['project.company.name'] = cfv;
    }
    const completedAt = determineDateRange(completedFrom, completedTo);
    if (completedAt.length) {
      additionalQuery['simulation.finishedAt'] = completedAt;
    }
    fetchResults({ page: page || 0, size: PAGE_SIZE }, trimToNull(filter), additionalQuery);
  };

  debouncedLoadResults = () => {
    if (this._filterChangeTimeout) {
      clearTimeout(this._filterChangeTimeout);
    }
    this._filterChangeTimeout = setTimeout(this.loadResults, 300);
  };

  handleFilterChange = (e) => this.setState({ filter: e.target.value }, this.debouncedLoadResults);
  handleCurrentPageChange = (p) => this.loadResults(p);

  handlePropToggle = (prop, key, callback) => {
    const {
      [prop]: { [key]: enabled, ...props },
    } = this.state;
    if (!enabled) {
      props[key] = true;
    }
    this.setState({ [prop]: props }, callback);
  };

  handleStatusToggle = (key) => this.handlePropToggle('statuses', key, this.loadResults);
  handleAssessmentToggle = (key) => this.handlePropToggle('assessments', key, this.loadResults);
  handleProjectFilterValueChange = (e) =>
    this.setState({ projectFilterValue: e.target.value }, this.debouncedLoadResults);
  handleCompanyFilterValueChange = (e) =>
    this.setState({ companyFilterValue: e.target.value }, this.debouncedLoadResults);
  handleCompletedFromChange = (e) => this.setState({ completedFrom: e.target.value }, this.loadResults);
  handleCompletedToChange = (e) => this.setState({ completedTo: e.target.value }, this.loadResults);

  render() {
    const {
      results: { items, page },
    } = this.props;
    const {
      filter,
      statuses,
      assessments,
      projectFilterValue,
      companyFilterValue,
      completedFrom,
      completedTo,
    } = this.state;
    return (
      <div className="flex flex1">
        <Grid>
          <GridHeader>
            <GridRow className="row">
              <GridCell className="col-sm-4">
                <span>Name</span>
              </GridCell>
              <GridCell className="col-sm-4">
                <span>Project</span>
              </GridCell>
              <GridCell className="col-sm-1">
                <span>Status</span>
              </GridCell>
              <GridCell className="col-sm-3">
                <span>Timestamp</span>
              </GridCell>
            </GridRow>
            <GridRow className="row" style={{ padding: 0 }}>
              <GridCell className="col-sm-12" style={{ padding: 0 }}>
                <PlainTextField
                  placeholder="Filter..."
                  value={filter}
                  onChange={this.handleFilterChange}
                  label="Filter"
                />
              </GridCell>
            </GridRow>
            <GridRow className="row" style={{ padding: 0, background: 'whitesmoke' }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  fontSize: '13px',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                }}
              >
                <ActivityStatusPanel statuses={statuses} onToggle={this.handleStatusToggle} />
                <AssessmentTypePanel assessments={assessments} onToggle={this.handleAssessmentToggle} />
                <CompanyFilterPanel value={companyFilterValue} onChange={this.handleCompanyFilterValueChange} />
                <ProjectFilterPanel value={projectFilterValue} onChange={this.handleProjectFilterValueChange} />
                <SimpleDateFilterPanel
                  label="Completed between:"
                  onFromChange={this.handleCompletedFromChange}
                  onToChange={this.handleCompletedToChange}
                  from={completedFrom}
                  to={completedTo}
                />
              </div>
            </GridRow>
            {/*<GridRow className="row" style={{ padding: 0 }}>*/}
            {/*  <GridCell className="col-sm-12" style={{ padding: 0 }}>*/}
            {/*    <ResultStats />*/}
            {/*  </GridCell>*/}
            {/*</GridRow>*/}
          </GridHeader>
          <ScrollArea smoothScrolling>
            <GridBody>
              {R.addIndex(R.map)(
                (r, idx) => (
                  <Row key={`${r.project.id}/${r.id}/${idx}`} {...r} />
                ),
                items
              )}
            </GridBody>
          </ScrollArea>
          <GridFooter>
            <GridRow className="Grid-pagination row">
              {page && (
                <GridCell className="col-sm-12 text-right">
                  <span>
                    {page.number * page.size + 1}-{Math.min(page.totalElements, (page.number + 1) * page.size)} of{' '}
                    {page.totalElements}
                  </span>
                  <IconButton onClick={() => this.handleCurrentPageChange(page.number - 1)} disabled={page.number <= 0}>
                    <IconLeft />
                  </IconButton>
                  <IconButton
                    onClick={() => this.handleCurrentPageChange(page.number + 1)}
                    disabled={(page.number + 1) * page.size > page.totalElements}
                  >
                    <IconRight />
                  </IconButton>
                </GridCell>
              )}
            </GridRow>
          </GridFooter>
        </Grid>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({ results: state.entities.results });

export default connect(mapStateToProps, resultsActions)(ResultsContainer);



// WEBPACK FOOTER //
// ./src/containers/private/ResultsContainer.js